import axios, { AxiosResponse } from "axios";
import TokenManagerHelper from "./token_manager_helper";

// Configuração da URL base da API de autenticação
const getAuthApiBaseURL = (): string => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  if (process.env.NODE_ENV === "development") {
    return "http://localhost:3000";
  }

  return "https://appdav-qa.coffcode.com";
};

const AUTH_API_BASE_URL = getAuthApiBaseURL();

interface LoginCredentials {
  proprietario: string;
  cpfCnpj: string;
  password: string;
}

interface LoginPayload {
  ownerDocument: string;
  username: string;
  password: string;
}

interface LoginResponse {
  message: string;
  token: string;
  user: {
    uid?: number;
    id?: number;
    uuid?: string;
    name?: string;
    username: string;
    role: string;
    email: string;
    cpfCnpj: string;
  };
}

interface RegisterData {
  username: string;
  email: string;
  password: string;
  cpfCnpj: string;
}

interface ForgotPasswordData {
  cpfCnpj: string;
}

class RealAuthService {
  private readonly baseURL: string;

  constructor(baseURL: string = AUTH_API_BASE_URL) {
    this.baseURL = baseURL;
  }

  /**
   * Login com credenciais
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      if (!credentials?.proprietario || !credentials?.cpfCnpj || !credentials?.password) {
        throw new Error("Credenciais inválidas: proprietario, cpfCnpj e password são obrigatórios");
      }

      const loginPayload = {
        ownerDocument: credentials.proprietario,
        username: credentials.cpfCnpj,
        password: credentials.password,
      };

      const requestConfig = {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      };

      const response: AxiosResponse<LoginResponse> = await axios.post(
        `${this.baseURL}/api/v1/auth/login`,
        loginPayload,
        requestConfig
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message || error.message;
        throw new Error(`Erro no login: ${message}`);
      }
      throw new Error("Erro desconhecido no login");
    }
  }

  /**
   * Registro de novo usuário
   */
  async register(userData: RegisterData): Promise<any> {
    try {
      const response: AxiosResponse<any> = await axios.post(
        `${this.baseURL}/api/v1/auth/register`,
        userData,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message || error.message;
        throw new Error(`Erro no registro: ${message}`);
      }
      throw new Error("Erro desconhecido no registro");
    }
  }

  /**
   * Esqueci minha senha
   */
  async forgotPassword(data: ForgotPasswordData): Promise<any> {
    try {
      const response: AxiosResponse<any> = await axios.post(
        `${this.baseURL}/api/v1/auth/forgot-password`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message || error.message;
        throw new Error(`Erro ao solicitar reset: ${message}`);
      }
      throw new Error("Erro desconhecido ao solicitar reset");
    }
  }

  /**
   * Atualizar perfil
   */
  async updateProfile(userData: any, token: string): Promise<any> {
    try {
      const response: AxiosResponse<any> = await axios.put(
        `${this.baseURL}/api/v1/auth/profile`,
        userData,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message || error.message;
        throw new Error(`Erro ao atualizar perfil: ${message}`);
      }
      throw new Error("Erro desconhecido ao atualizar perfil");
    }
  }

  /**
   * Configurar token global para futuras requisições
   */
  setAuthToken(token: string): void {
    TokenManagerHelper.setAuthToken(token);
  }

  /**
   * Remover token global
   */
  clearAuthToken(): void {
    TokenManagerHelper.clearAuthToken();
  }
}

// Instância singleton
export const realAuthService = new RealAuthService();

export default realAuthService;
